import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ExternalLink, Globe, Link2, MapPin, Tag } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Spinner, EmptyState } from "@/components/Bits";
import { AdCard } from "@/components/AdSlot";

const ORIGIN = process.env.REACT_APP_BACKEND_URL;
const CATS = { restauration: "Restauration", bar_cafe: "Bar & café", commerce: "Commerce", ecommerce: "Boutique en ligne", sport: "Sport", habillement: "Habillement",
  services: "Services", numerique: "Numérique", loisirs: "Loisirs", evenements: "Événements", b2b: "Services aux entreprises", tourisme: "Tourisme", autre: "Autre" };

function Locations({ partner }) {
  if (!partner.locations.length) {
    return partner.online_only ? <p className="text-sm text-slate-400" data-testid="partner-page-online">Ce partenaire est disponible uniquement en ligne.</p> : null;
  }
  return (
    <ul className="grid gap-2 sm:grid-cols-2" data-testid="partner-page-locations">
      {partner.locations.map((l, i) => (
        <li key={i} className="flex gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-3" data-testid={`partner-page-location-${i}`}>
          <MapPin size={16} className="mt-0.5 shrink-0 text-[#B7F52A]" />
          <div className="min-w-0 text-sm">
            <p className="font-semibold text-white [overflow-wrap:anywhere]">{l.name}</p>
            {l.address && <p className="text-slate-400 [overflow-wrap:anywhere]">{l.address}</p>}
            {(l.city || l.region) && <p className="text-slate-400">{[l.city, l.region].filter(Boolean).join(", ")}</p>}
          </div>
        </li>
      ))}
    </ul>
  );
}

function ShareButton({ partner }) {
  const share = async () => {
    const url = `${window.location.origin}/offres/partenaire/${partner.id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Lien copié : envoie-le à ta ligue");
    } catch {
      toast.error("Impossible de copier le lien");
    }
  };
  return (
    <button type="button" onClick={share} className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1 text-sm text-slate-200 transition-colors hover:bg-white/5" data-testid="partner-page-share">
      <Link2 size={14} /> Copier le lien
    </button>
  );
}

function Header({ partner }) {
  return (
    <header className="flex flex-col gap-4 sm:flex-row sm:items-center">
      <span className="flex h-24 w-24 shrink-0 items-center justify-center rounded-3xl bg-white p-3">
        {partner.logo_url && <img src={`${ORIGIN}${partner.logo_url}`} alt={`Logo ${partner.name}`} className="max-h-full max-w-full object-contain" data-testid="partner-page-logo" />}
      </span>
      <div className="min-w-0 space-y-1">
        <p className="mpl-eyebrow">{CATS[partner.category] || "Partenaire"}</p>
        <h1 className="font-display text-3xl font-bold text-white [overflow-wrap:anywhere] sm:text-4xl" data-testid="partner-page-name">{partner.name}</h1>
        <div className="flex flex-wrap items-center gap-3 pt-1">
        <ShareButton partner={partner} />
        {partner.website && (
          <a href={partner.website} target="_blank" rel="noopener noreferrer nofollow" className="inline-flex items-center gap-1.5 text-sm text-[#B7F52A] hover:underline" data-testid="partner-page-website">
            <Globe size={14} /> Site du partenaire <ExternalLink size={12} />
          </a>
        )}
        </div>
      </div>
    </header>
  );
}

export default function PartnerPage() {
  const { id } = useParams();
  const [partner, setPartner] = useState(undefined);
  const [ads, setAds] = useState(null);
  useEffect(() => {
    setPartner(undefined);
    setAds(null);
    api.get(`/ads/partner-page/${id}`).then(({ data }) => setPartner(data)).catch(() => setPartner(null));
    api.get("/ads/serve", { params: { placement: "offers_page", partner_id: id } }).then(({ data }) => setAds(data.ads || [])).catch(() => setAds([]));
  }, [id]);
  const back = <Link to="/offres" className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white" data-testid="partner-page-back"><ArrowLeft size={14} /> Offres partenaires</Link>;
  if (partner === undefined) return <Spinner />;
  if (!partner) return <div className="space-y-4" data-testid="partner-page-missing">{back}<EmptyState icon={Tag} title="Partenaire introuvable" text="Ce partenaire n'a pas de page disponible en ce moment." testId="partner-page-missing-empty" /></div>;
  return (
    <div className="space-y-7" data-testid="partner-page">
      {back}
      <Header partner={partner} />
      {partner.description && <p className="max-w-2xl whitespace-pre-line text-sm text-slate-300 [overflow-wrap:anywhere]" data-testid="partner-page-description">{partner.description}</p>}
      <section className="space-y-3">
        <p className="mpl-eyebrow">Établissements</p>
        <Locations partner={partner} />
        {!partner.locations.length && !partner.online_only && <p className="text-sm text-slate-500" data-testid="partner-page-no-locations">Aucun établissement renseigné pour le moment.</p>}
      </section>
      <section className="space-y-3">
        <p className="mpl-eyebrow">Offres en cours</p>
        {!ads ? <Spinner /> : ads.length ? <div className="grid gap-3 md:grid-cols-2" data-testid="partner-page-offers">{ads.map((a) => <AdCard key={a.token} ad={a} />)}</div>
          : <EmptyState icon={Tag} title="Aucune offre pour toi en ce moment" text="Ses offres actuelles visent peut-être une autre région ou un autre type de ligue." testId="partner-page-offers-empty" />}
      </section>
      <p className="text-xs text-slate-500">Les offres partenaires n'ont aucun effet sur tes points ni sur les classements. Ma Petite Ligue ne transmet jamais tes coordonnées aux partenaires.</p>
    </div>
  );
}
